import React, { useCallback, useEffect, useState } from 'react';
import { actions, feedbackV2, inputs, overlays } from '@ecosystem/newron-design-system';
import { CustomMenuModalSettings, MenuItemOption } from '../../../types';

const { Modal } = overlays;
const { Button } = actions;
const { NumberInput } = inputs;
const { Alert } = feedbackV2;

export interface CustomTimeSeriesDurationModalProps {
  menuItemOption: MenuItemOption;
  setMenuCustomItem: (value: MenuItemOption) => void;
  isOpen: boolean;
  onClose: () => void;
  currentSelectedPeriods?: string[];
  customMenuModalSettings: CustomMenuModalSettings;
  onApply: (value: string) => void;
}

export const CustomTimeSeriesModal: React.FC<CustomTimeSeriesDurationModalProps> = ({
  menuItemOption,
  setMenuCustomItem,
  isOpen,
  onClose,
  customMenuModalSettings,
  onApply,
  currentSelectedPeriods,
}) => {
  const [selectedCustomTimeSeriesValue, setSelectedCustomTimeSeriesValue] = useState<number>(0);
  const [customLabel, setCustomLabel] = useState<string>('');
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const { customMenuLabels, maxDuration } = customMenuModalSettings.customTimeSeriesDuration;

  const customTimeSeriesLabel: string[] = customMenuLabels.split('|');

  const [_, currentCustomDuration] = currentSelectedPeriods ?? [];

  useEffect(() => {
    const currentDuration = parseInt(currentCustomDuration);
    const validCurrentDuration = isNaN(currentDuration) ? 0 : currentDuration;
    setSelectedCustomTimeSeriesValue(validCurrentDuration);
  }, [currentCustomDuration]);

  const handleOnChange = useCallback((selectedValue: number) => {
    const label = `Custom (${selectedValue}${customTimeSeriesLabel[1]})`;

    if (selectedValue < 0 || selectedValue > maxDuration) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
      setSelectedCustomTimeSeriesValue(selectedValue);
      setCustomLabel(label);
    }
  }, []);

  const handleOnClick = () => {
    if (customMenuModalSettings.shouldUpdateLabel) {
      menuItemOption.label = customLabel;
      setMenuCustomItem(menuItemOption);
    }

    onApply(selectedCustomTimeSeriesValue.toString());
  };

  return (
    <Modal size="md" isOpen={isOpen} onClose={onClose}>
      <Modal.Header>Custom time series</Modal.Header>
      <div className="px-6 pt-4">
        {isDisabled && (
          <Alert title={`Time series is limited to ${maxDuration} ${customTimeSeriesLabel[1]}`} type={'info'} />
        )}
      </div>
      <div className="flex flex-row flex-nowrap px-6 pt-4 pb-6 gap-4">
        <div className="self-center">{customTimeSeriesLabel[0]}</div>
        <div className="w-20 self-center grow-0">
          <NumberInput
            key={`custom-time-series`}
            value={selectedCustomTimeSeriesValue}
            onChange={handleOnChange}
            testId={`custom-time-series`}
          />
        </div>
        <div className="self-center">{customTimeSeriesLabel[1]}</div>
      </div>
      <Modal.Footer>
        <Button mode="secondary" onClick={onClose} testId="modal-cancel-btn">
          Cancel
        </Button>
        <Button disabled={isDisabled} onClick={handleOnClick} testId="modal-apply-btn">
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
